import { useEffect, useState } from "react";
import { Fragment } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API, fmt, fmtInt, fmtNum, amountInWords } from "@/lib/api";
import { Download, Printer, Mail, MessageCircle, Pencil, History, Ban, RotateCcw, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const LOGO_URL =
    "https://customer-assets-jai6qajn.emergentagent.net/job_pdf-guide-dev/artifacts/7s3f15d0_HDB%20LOGO.webp";

export default function InvoiceDetail() {
    const { id } = useParams();
    const { user: me } = useAuth();
    const isAdmin = me?.role === "admin";
    const [inv, setInv] = useState(null);
    const [co, setCo] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [showCancel, setShowCancel] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get(`/invoices/${id}`).then((r) => setInv(r.data));
        api.get("/company").then((r) => setCo(r.data));
    }, [id]);

    if (!inv || !co) return <div>Loading…</div>;

    const isCancelled = inv.status === "cancelled";

    const history = inv.history || [];
    const editCount = history.filter((h) => h.action === "edited").length;
    const lastEdit = [...history].reverse().find((h) => h.action === "edited");

    const grouped = inv.line_items.reduce((acc, li) => {
        (acc[li.category] = acc[li.category] || []).push(li);
        return acc;
    }, {});
    const isB2C = inv.invoice_type === "B2C";

    // Version stamp so a browser/CDN never serves a copy from before the last edit.
    const ver = encodeURIComponent(inv.updated_at || inv.created_at || "");
    const pdfUrl = `${API}/invoices/${id}/pdf?v=${ver}`;
    const receivingSheetUrl = `${API}/invoices/${id}/receiving-sheet.pdf?v=${ver}`;

    return (
        <div data-testid="invoice-detail">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3 no-print">
                <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Invoice</div>
                    <h1 className="text-3xl hdb-heading font-black text-slate-900 hdb-mono">{inv.invoice_number}</h1>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {!isCancelled && (
                        <Link to={`/invoices/${id}/edit`} className="hdb-btn hdb-btn-ghost" data-testid="edit-invoice-btn">
                            <Pencil size={14} /> Edit
                        </Link>
                    )}
                    <a href={pdfUrl} target="_blank" rel="noreferrer" className="hdb-btn hdb-btn-primary" data-testid="download-pdf-btn">
                        <Download size={14} /> Download PDF
                    </a>
                    <a href={receivingSheetUrl} target="_blank" rel="noreferrer"
                       className="hdb-btn hdb-btn-mint"
                       title="Print-friendly delivery receiving sheet (no pricing / GST) — hand to delivery staff"
                       data-testid="receiving-sheet-btn">
                        <ClipboardCheck size={14} /> Receiving Sheet
                    </a>
                    <button className="hdb-btn hdb-btn-ghost" onClick={() => window.print()} data-testid="print-btn">
                        <Printer size={14} /> Print
                    </button>
                    <button className="hdb-btn hdb-btn-ghost" onClick={() => toast("Email delivery — coming soon")}
                            data-testid="email-btn">
                        <Mail size={14} /> Email
                    </button>
                    <button className="hdb-btn hdb-btn-ghost" onClick={() => toast("WhatsApp send — coming soon")}
                            data-testid="whatsapp-btn">
                        <MessageCircle size={14} /> WhatsApp
                    </button>
                    {isAdmin && !isCancelled && (
                        <button className="hdb-btn hdb-btn-danger" onClick={() => setShowCancel(true)}
                                data-testid="cancel-invoice-btn">
                            <Ban size={14} /> Cancel Invoice
                        </button>
                    )}
                    {isAdmin && isCancelled && (
                        <button className="hdb-btn hdb-btn-ghost" onClick={async () => {
                            setBusy(true);
                            try {
                                await api.post(`/invoices/${id}/uncancel`);
                                const r = await api.get(`/invoices/${id}`);
                                setInv(r.data);
                                toast.success("Invoice reactivated");
                            } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                            setBusy(false);
                        }} disabled={busy} data-testid="uncancel-invoice-btn">
                            <RotateCcw size={14} /> Reactivate
                        </button>
                    )}
                </div>
            </div>

            {isCancelled && (
                <div className="mb-4 rounded-lg border-2 border-red-300 bg-red-50 p-4 no-print"
                     data-testid="cancelled-banner">
                    <div className="flex items-start gap-3">
                        <Ban size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <div className="hdb-heading font-black text-lg text-red-900">INVOICE CANCELLED</div>
                            <div className="text-sm text-red-800 mt-1">
                                <b>Reason:</b> {inv.cancellation_reason || "—"}
                            </div>
                            <div className="text-xs text-red-700 mt-1 hdb-mono">
                                Cancelled by <b>{inv.cancelled_by || "—"}</b>
                                {inv.cancelled_at && ` on ${new Date(inv.cancelled_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}`}
                            </div>
                            <div className="text-xs text-red-700 mt-2">
                                This invoice is excluded from all accounting, GST and reporting calculations.
                                Its invoice number is preserved for GST compliance and audit.
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showCancel && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm"
                     data-testid="cancel-modal">
                    <div className="hdb-card w-full max-w-md p-6 shadow-2xl">
                        <div className="flex items-center gap-2 mb-3">
                            <Ban size={20} className="text-red-600" />
                            <h3 className="hdb-heading font-bold text-lg text-slate-900">Cancel this invoice?</h3>
                        </div>
                        <p className="text-sm text-slate-600 mb-4">
                            The invoice will keep its number (<span className="hdb-mono font-semibold">{inv.invoice_number}</span>)
                            for GST compliance but will be removed from every accounting and tax calculation. A
                            <b> CANCELLED </b> watermark will appear on the PDF.
                        </p>
                        <label className="hdb-label">Reason (required, audit-logged)</label>
                        <textarea className="hdb-input" rows={3} value={cancelReason}
                                  onChange={(e) => setCancelReason(e.target.value)}
                                  placeholder="e.g. Duplicate invoice, buyer requested changes, wrong pricing…"
                                  data-testid="cancel-reason-input" />
                        <div className="flex justify-end gap-2 mt-4">
                            <button className="hdb-btn hdb-btn-ghost" onClick={() => { setShowCancel(false); setCancelReason(""); }}>
                                Keep active
                            </button>
                            <button className="hdb-btn hdb-btn-danger" disabled={busy || !cancelReason.trim()}
                                    data-testid="confirm-cancel-btn"
                                    onClick={async () => {
                                        setBusy(true);
                                        try {
                                            await api.post(`/invoices/${id}/cancel`, { reason: cancelReason.trim() });
                                            const r = await api.get(`/invoices/${id}`);
                                            setInv(r.data);
                                            toast.success(`Invoice ${inv.invoice_number} cancelled`);
                                            setShowCancel(false);
                                            setCancelReason("");
                                        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                                        setBusy(false);
                                    }}>
                                {busy ? "Cancelling…" : "Cancel invoice"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Change log */}
            {history.length > 0 && (
                <div className="no-print mb-4 hdb-card">
                    <button onClick={() => setShowHistory(!showHistory)}
                            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
                            data-testid="toggle-history-btn">
                        <div className="flex items-center gap-2 text-sm">
                            <History size={14} className="text-slate-500" />
                            <span className="font-semibold text-slate-900">Change log</span>
                            <span className="text-xs text-slate-500">
                                {editCount === 0
                                    ? "Never edited"
                                    : `Edited ${editCount} time${editCount > 1 ? "s" : ""}${lastEdit ? " · last on " + new Date(lastEdit.at).toLocaleString("en-IN") : ""}`}
                            </span>
                        </div>
                        <span className="text-xs text-slate-500">{showHistory ? "Hide" : "Show"}</span>
                    </button>
                    {showHistory && (
                        <div className="border-t border-slate-100 divide-y divide-slate-100" data-testid="history-list">
                            {[...history].reverse().map((h, i) => (
                                <div key={i} className="px-4 py-3 text-xs">
                                    <div className="flex items-baseline justify-between mb-1">
                                        <span className={`hdb-tag ${h.action === "created" ? "hdb-tag-mint" : "hdb-tag-gold"}`}>
                                            {h.action === "created" ? "Created" : "Edited"}
                                        </span>
                                        <span className="hdb-mono text-slate-500">
                                            {new Date(h.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                                        </span>
                                    </div>
                                    {h.changes && h.changes.length > 0 ? (
                                        <ul className="mt-2 space-y-1">
                                            {h.changes.map((c, j) => (
                                                <li key={j} className="grid grid-cols-[140px_1fr] gap-2">
                                                    <span className="text-slate-500 hdb-mono">{c.field}</span>
                                                    {c.field === "items_removed" || c.field === "items_added" ? (
                                                        <span className={`font-semibold ${c.field === "items_removed" ? "text-red-700" : "text-emerald-700"}`}
                                                              data-testid={`history-${c.field}`}>
                                                            {c.field === "items_removed" ? `− ${c.old}` : `+ ${c.new}`}
                                                        </span>
                                                    ) : (
                                                    <span className="text-slate-800">
                                                        <span className="line-through text-slate-400">{String(c.old) || "—"}</span>
                                                        {" → "}
                                                        <span className="font-semibold text-slate-900">{String(c.new) || "—"}</span>
                                                    </span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        h.action === "created" && (
                                            <div className="text-slate-500 mt-1">Invoice was created.</div>
                                        )
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* A4 preview */}
            <div className="bg-white shadow-lg mx-auto p-10 border border-slate-200" style={{ maxWidth: "210mm", minHeight: "297mm" }}>
                {/* Header */}
                <div className="flex justify-between items-start pb-4 border-b-4 border-slate-900">
                    <img src={LOGO_URL} alt="HDB" className="h-16" />
                    <div className="text-right">
                        <div className="hdb-heading font-black text-slate-900 text-xl">{co.name}</div>
                        <div className="text-xs text-slate-600 leading-relaxed mt-1">
                            {co.address}<br />
                            GSTIN: <span className="hdb-mono font-semibold">{co.gstin}</span> · State: {co.state} ({co.state_code})<br />
                            {co.contact}
                        </div>
                    </div>
                </div>

                <div className="bg-slate-900 text-white px-4 py-2 mt-4 flex justify-between text-sm font-bold hdb-heading">
                    <span>TAX INVOICE · {inv.invoice_type}</span>
                    <span>{inv.is_interstate ? "IGST (Inter-State)" : "CGST + SGST (Intra-State)"}</span>
                </div>

                {/* Meta */}
                <div className="grid grid-cols-2 gap-4 mt-4 border border-slate-200">
                    <div className="p-4 border-r border-slate-200">
                        <div className="hdb-label mb-1">Bill To</div>
                        <div className="font-bold text-slate-900">{inv.buyer.name}</div>
                        <div className="text-xs text-slate-600 mt-1 leading-relaxed">
                            {inv.buyer.address}<br />
                            {inv.buyer.state && <>State: {inv.buyer.state} ({inv.buyer.state_code})<br /></>}
                            {inv.buyer.gstin && <>GSTIN: <span className="hdb-mono">{inv.buyer.gstin}</span><br /></>}
                            {inv.buyer.contact}
                        </div>
                    </div>
                    <div className="p-4 text-sm space-y-1">
                        <div><span className="hdb-label inline-block w-32">Invoice No.</span> <span className="hdb-mono font-bold">{inv.invoice_number}</span></div>
                        <div><span className="hdb-label inline-block w-32">Date</span> <span className="hdb-mono">{inv.invoice_date}</span></div>
                        <div><span className="hdb-label inline-block w-32">Place of Supply</span> {inv.buyer.state || "-"} ({inv.buyer.state_code || "-"})</div>
                    </div>
                </div>

                {/* Lines */}
                <table className="w-full mt-4 text-xs" style={{ borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <colgroup>
                        {isB2C ? (
                            <>
                                <col style={{ width: "4%" }} />
                                <col style={{ width: "24%" }} />
                                <col style={{ width: "8%" }} />
                                <col style={{ width: "6%" }} />
                                <col style={{ width: "6%" }} />
                                <col style={{ width: "9%" }} />
                                <col style={{ width: "9%" }} />
                                <col style={{ width: "6%" }} />
                                <col style={{ width: "9%" }} />
                                <col style={{ width: "9%" }} />
                                <col style={{ width: "10%" }} />
                            </>
                        ) : (
                            <>
                                <col style={{ width: "5%" }} />
                                <col style={{ width: "30%" }} />
                                <col style={{ width: "9%" }} />
                                <col style={{ width: "8%" }} />
                                <col style={{ width: "8%" }} />
                                <col style={{ width: "10%" }} />
                                <col style={{ width: "8%" }} />
                                <col style={{ width: "11%" }} />
                                <col style={{ width: "11%" }} />
                            </>
                        )}
                    </colgroup>
                    <thead>
                        <tr className="bg-slate-900 text-white">
                            <th className="px-2 py-2 text-center" style={{ width: 28 }}>#</th>
                            <th className="px-2 py-2 text-left">Description</th>
                            <th className="px-2 py-2">HSN</th>
                            <th className="px-2 py-2">Qty</th>
                            <th className="px-2 py-2">Unit</th>
                            {isB2C && <th className="px-2 py-2 text-right">MRP</th>}
                            <th className="px-2 py-2 text-right">Rate</th>
                            <th className="px-2 py-2 text-right">GST%</th>
                            <th className="px-2 py-2 text-right">GST Amt</th>
                            {isB2C && <th className="px-2 py-2 text-right">Bachat</th>}
                            <th className="px-2 py-2 text-right">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(() => {
                            let sl = 0;
                            return Object.entries(grouped).map(([cat, lis], gi) => (
                                <Fragment key={`grp-${gi}`}>
                                    <tr className="bg-slate-100">
                                        <td colSpan={isB2C ? 11 : 9} className="px-2 py-1.5 font-bold hdb-heading text-slate-900" style={{ border: "1px solid #E2E8F0" }}>
                                            {cat}
                                        </td>
                                    </tr>
                                    {lis.map((li, i) => (
                                        <tr key={`li-${gi}-${i}`} className="border-b border-slate-200">
                                            <td className="px-2 py-1.5 text-center hdb-mono" style={{ border: "1px solid #E2E8F0" }}>{++sl}</td>
                                            <td className="px-2 py-1.5 align-top" style={{ border: "1px solid #E2E8F0", wordBreak: "break-word", whiteSpace: "normal" }}>{li.name}</td>
                                            <td className="px-2 py-1.5 hdb-mono text-right" style={{ border: "1px solid #E2E8F0" }}>{li.hsn}</td>
                                            <td className="px-2 py-1.5 hdb-mono text-right" style={{ border: "1px solid #E2E8F0" }}>{li.qty}</td>
                                            <td className="px-2 py-1.5 text-center" style={{ border: "1px solid #E2E8F0" }}>{li.unit}</td>
                                            {isB2C && (
                                                <td className="px-2 py-1.5 hdb-mono text-right" style={{ border: "1px solid #E2E8F0" }}>{fmtNum(li.mrp || 0)}</td>
                                            )}
                                            <td className="px-2 py-1.5 hdb-mono text-right" style={{ border: "1px solid #E2E8F0" }}>{fmtNum(li.rate)}</td>
                                            <td className="px-2 py-1.5 hdb-mono text-right" style={{ border: "1px solid #E2E8F0" }}>{li.tax_percent}%</td>
                                            <td className="px-2 py-1.5 hdb-mono text-right" style={{ border: "1px solid #E2E8F0" }}>{fmtNum(li.tax_amount)}</td>
                                            {isB2C && (
                                                <td className="px-2 py-1.5 hdb-mono text-right text-emerald-700 font-semibold" style={{ border: "1px solid #E2E8F0" }}>{fmtNum(li.bachat || 0)}</td>
                                            )}
                                            <td className="px-2 py-1.5 hdb-mono text-right font-semibold" style={{ border: "1px solid #E2E8F0" }}>{fmtNum(li.line_total)}</td>
                                        </tr>
                                    ))}
                                </Fragment>
                            ));
                        })()}
                    </tbody>
                </table>

                {/* Summary */}
                <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="col-span-2">
                        <div className="hdb-label mb-2">{isB2C ? "Savings Summary" : "Tax Summary"}</div>
                        {isB2C ? (
                            <table className="w-full text-xs" style={{ borderCollapse: "collapse" }}>
                                <tbody>
                                    <tr>
                                        <td className="px-3 py-2 font-semibold" style={{ border: "1px solid #E2E8F0" }}>Your Previous Savings</td>
                                        <td className="px-3 py-2 hdb-mono text-right" style={{ border: "1px solid #E2E8F0" }}>{fmt(inv.previous_savings || 0)}</td>
                                    </tr>
                                    <tr>
                                        <td className="px-3 py-2 font-semibold" style={{ border: "1px solid #E2E8F0" }}>Your Saving Today</td>
                                        <td className="px-3 py-2 hdb-mono text-right" style={{ border: "1px solid #E2E8F0" }}>{fmt(inv.today_savings || 0)}</td>
                                    </tr>
                                    <tr className="bg-emerald-500 text-white">
                                        <td className="px-3 py-2 hdb-heading font-black" style={{ border: "1px solid #E2E8F0" }}>TOTAL SAVINGS TILL NOW</td>
                                        <td className="px-3 py-2 hdb-mono text-right font-black" style={{ border: "1px solid #E2E8F0" }}>{fmt(inv.total_savings || 0)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        ) : (
                            <table className="w-full text-xs" style={{ borderCollapse: "collapse" }}>
                            <thead>
                                <tr className="bg-slate-100">
                                    <th className="px-2 py-1.5 text-left" style={{ border: "1px solid #E2E8F0" }}>Rate</th>
                                    <th className="px-2 py-1.5 text-right" style={{ border: "1px solid #E2E8F0" }}>Taxable Value</th>
                                    {inv.is_interstate ? (
                                        <th className="px-2 py-1.5 text-right" style={{ border: "1px solid #E2E8F0" }}>IGST</th>
                                    ) : (
                                        <>
                                            <th className="px-2 py-1.5 text-right" style={{ border: "1px solid #E2E8F0" }}>CGST</th>
                                            <th className="px-2 py-1.5 text-right" style={{ border: "1px solid #E2E8F0" }}>SGST</th>
                                        </>
                                    )}
                                    <th className="px-2 py-1.5 text-right" style={{ border: "1px solid #E2E8F0" }}>Total Tax</th>
                                </tr>
                            </thead>
                            <tbody>
                                {inv.tax_summary.map((t, i) => (
                                    <tr key={i}>
                                        <td className="px-2 py-1.5 hdb-mono" style={{ border: "1px solid #E2E8F0" }}>{t.rate}%</td>
                                        <td className="px-2 py-1.5 hdb-mono text-right" style={{ border: "1px solid #E2E8F0" }}>{fmtNum(t.taxable)}</td>
                                        {inv.is_interstate ? (
                                            <td className="px-2 py-1.5 hdb-mono text-right" style={{ border: "1px solid #E2E8F0" }}>{fmtNum(t.igst)}</td>
                                        ) : (
                                            <>
                                                <td className="px-2 py-1.5 hdb-mono text-right" style={{ border: "1px solid #E2E8F0" }}>{fmtNum(t.cgst)}</td>
                                                <td className="px-2 py-1.5 hdb-mono text-right" style={{ border: "1px solid #E2E8F0" }}>{fmtNum(t.sgst)}</td>
                                            </>
                                        )}
                                        <td className="px-2 py-1.5 hdb-mono text-right font-semibold" style={{ border: "1px solid #E2E8F0" }}>{fmtNum(t.total_tax)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        )}
                    </div>
                    <div>
                        <table className="w-full text-sm">
                            <tbody>
                                <tr><td className="py-1.5">Subtotal</td>
                                    <td className="py-1.5 text-right hdb-mono">{fmt(inv.subtotal)}</td></tr>
                                {inv.is_interstate ? (
                                    <tr><td className="py-1.5">IGST</td>
                                        <td className="py-1.5 text-right hdb-mono">{fmt(inv.igst)}</td></tr>
                                ) : (
                                    <>
                                        <tr><td className="py-1.5">CGST</td>
                                            <td className="py-1.5 text-right hdb-mono">{fmt(inv.cgst)}</td></tr>
                                        <tr><td className="py-1.5">SGST</td>
                                            <td className="py-1.5 text-right hdb-mono">{fmt(inv.sgst)}</td></tr>
                                    </>
                                )}
                                {inv.delivery > 0 && (
                                    <tr><td className="py-1.5">Delivery</td>
                                        <td className="py-1.5 text-right hdb-mono">{fmt(inv.delivery)}</td></tr>
                                )}
                                {Math.abs(inv.round_off || 0) >= 0.01 && (
                                    <tr><td className="py-1.5">Round Off</td>
                                        <td className="py-1.5 text-right hdb-mono">
                                            {inv.round_off > 0 ? "+" : "−"} {fmtNum(Math.abs(inv.round_off))}
                                        </td></tr>
                                )}
                                <tr className="bg-slate-900 text-white">
                                    <td className="px-2 py-2 hdb-heading font-bold">Grand Total</td>
                                    <td className="px-2 py-2 text-right hdb-mono font-black">{fmtInt(inv.grand_total)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Amount in words */}
                <div className="mt-4 bg-slate-50 border border-slate-200 px-4 py-2 text-sm italic text-slate-800" data-testid="amount-words">
                    <span className="font-bold not-italic text-slate-900">Amount in words:</span> {amountInWords(inv.grand_total)}
                </div>

                {/* Terms + signature */}
                <div className="grid grid-cols-3 gap-4 mt-5">
                    <div className="col-span-2 text-[10.5px] text-slate-600 leading-relaxed border border-slate-200 p-3">
                        <div className="hdb-heading font-bold text-slate-900 text-sm mb-1">Terms &amp; Conditions</div>
                        1. Payment is due on delivery.<br/>
                        2. Goods may be returned or exchanged only for reasons of defective quality, damage in transit, incorrect item, or shortfall / excess in quantity — provided such issues are identified and reported at the time of delivery. No claims will be entertained thereafter.<br/>
                        3. All disputes are subject to the exclusive jurisdiction of the courts at Dehradun.<br/>
                        4. This is a computer-generated invoice and does not require a physical signature.
                    </div>
                    <div className="border border-slate-200 p-3 text-center text-xs">
                        <div className="text-slate-900 font-bold hdb-heading">For {co.name}</div>
                        <div className="mt-10 border-t border-slate-400 pt-1 text-slate-600">Authorised Signatory</div>
                    </div>
                </div>

                {/* Footer strip */}
                <div className="mt-5 text-center text-[10px] text-slate-500 border-t border-slate-200 pt-2">
                    Har Din Bachat — Everyday savings, every single day. · {co.contact}
                </div>
            </div>
        </div>
    );
}

function inWords(n) {
    // Kept for backwards compat but the page now uses amountInWords from lib/api
    return amountInWords(n);
}
